// src/components/PreferencesContent.tsx
/**
 * T-35: Preferences Content Component
 * Content component for the new UI framework Preferences Side Panel
 * Reorganised into logical collapsible sections
 */

import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Palette, 
  Ruler,
  MousePointer2,
  Share2,
  Shield,
  RotateCcw,
  Sun,
  Moon,
  Save,
  BarChart2,
  ChevronDown
} from 'lucide-react';
import { RootState } from '../store';
import { 
  setTheme,
  setLocale,
  setExportBackground,
  toggleAutoSave,
  toggleKeyboardShortcuts,
  toggleAccessibilityMode,
  setConstantsPrecision,
  setDefaultScaleFactor,
  setPreferredOrientation,
  toggleWheelReverseScroll,
  setWheelScrollSensitivity,
  setDefaultJumpSize,
  toggleInstallPrompt,
  setSidebarDefaultOpen,
  setTextScale,
  setSubdivisionMode,
  resetPreferences,
  type ThemeMode,
  type LocaleCode
} from '../store/slices/preferencesSlice';
import { 
  setMode as setEquivalentMode,
  setDenominators as setEquivalentDenominators,
  setIntegerLabelsOnly as setEquivalentIntegerLabelsOnly
} from '../store/slices/equivalentFractionsSlice';
import { SubdivisionMode } from '../types/redux';
import { InstallButton } from './InstallPrompt';
import { JumpLayoutSettings } from './JumpLayoutSettings';

/**
 * CollapsibleSection component for preferences grouping
 */
interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  iconColorClass: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  iconColorClass,
  defaultOpen = true,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <span className={iconColorClass}>{icon}</span>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className="collapsible-content transition-all duration-200 ease-in-out overflow-hidden"
        style={{
          maxHeight: isOpen ? contentHeight : 0,
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div ref={contentRef} className="p-4 space-y-4">
          {children}
        </div>
      </div>
    </section>
  );
};

/**
 * Toggle switch component for consistent styling
 */
interface ToggleSwitchProps {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  label,
  description,
}) => (
  <div className="flex items-center justify-between">
    <div className="flex-1 pr-4">
      <div className="font-medium text-gray-900 dark:text-white">
        {label}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400">
        {description}
      </div>
    </div>
    <button
      onClick={onChange}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors
        ${checked ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'}
      `}
      role="switch"
      aria-checked={checked}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${checked ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  </div>
);

export const PreferencesContent: React.FC = () => {
  const dispatch = useDispatch();
  const preferences = useSelector((state: RootState) => state.preferences);
  const viewport = useSelector((state: RootState) => state.viewport);
  const secondaryLine = useSelector((state: RootState) => state.secondaryLine);
  const constants = useSelector((state: RootState) => state.constants);
  const equivalentFractions = useSelector((state: RootState) => state.equivalentFractions);
  
  const [pendingChanges, setPendingChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [jumpSizeInput, setJumpSizeInput] = useState(preferences.defaultJumpSize.toString());
  const [denominatorInput, setDenominatorInput] = useState(
    equivalentFractions.denominators.join(', ')
  );
  const [denominatorError, setDenominatorError] = useState<string | null>(null);

  // Reset pendingChanges after a short delay to show save completion
  useEffect(() => {
    if (pendingChanges) {
      const timer = setTimeout(() => {
        setPendingChanges(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [pendingChanges]);

  // Update jump size input when preferences change
  useEffect(() => {
    setJumpSizeInput(preferences.defaultJumpSize.toString());
  }, [preferences.defaultJumpSize]);

  useEffect(() => {
    setDenominatorInput(equivalentFractions.denominators.join(', '));
  }, [equivalentFractions.denominators]);

  // Handle theme change
  const handleThemeChange = (theme: ThemeMode) => {
    dispatch(setTheme(theme));
    setPendingChanges(true);
  };

  // Handle export background change
  const handleExportBackgroundChange = (background: 'transparent' | 'white' | 'black') => {
    dispatch(setExportBackground(background));
    setPendingChanges(true);
  };

  // Handle orientation change
  const handleOrientationChange = (orientation: 'horizontal' | 'vertical') => {
    dispatch(setPreferredOrientation(orientation));
    setPendingChanges(true);
  };

  // Handle precision change
  const handlePrecisionChange = (precision: number) => {
    dispatch(setConstantsPrecision(precision));
    setPendingChanges(true);
  };

  // Handle scale factor change
  const handleScaleFactorChange = (numerator: number, denominator: number) => {
    dispatch(setDefaultScaleFactor({ n: numerator, d: denominator }));
    setPendingChanges(true);
  };

  // Handle default jump size change
  const handleDefaultJumpSizeChange = (jumpSize: number) => {
    dispatch(setDefaultJumpSize(jumpSize));
    setPendingChanges(true);
  };

  const handleJumpSizeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setJumpSizeInput(inputValue);
    
    const value = parseFloat(inputValue);
    if (!isNaN(value) && value >= 0.1 && value <= 100) {
      handleDefaultJumpSizeChange(value);
    }
  };

  const handleJumpSizeInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value) || value < 0.1) {
      handleDefaultJumpSizeChange(0.1);
      setJumpSizeInput('0.1');
    } else if (value > 100) {
      handleDefaultJumpSizeChange(100);
      setJumpSizeInput('100');
    } else {
      setJumpSizeInput(value.toString());
    }
  };

  const handleViewModeChange = (mode: 'standard' | 'equivalentFractions') => {
    dispatch(setEquivalentMode(mode));
    setPendingChanges(true);
  };

  const parseDenominatorList = (raw: string): number[] => {
    return Array.from(
      new Set(
        raw
          .split(/[,\s]+/)
          .map((token) => parseInt(token, 10))
          .filter((n) => Number.isFinite(n) && n > 0)
      )
    )
      .map((n) => Math.trunc(Math.abs(n)))
      .filter((n) => n > 0)
      .slice(0, 12);
  };

  const handleDenominatorSave = (value: string) => {
    const parsed = parseDenominatorList(value);
    if (parsed.length === 0) {
      setDenominatorError('Enter at least one positive integer denominator');
      return;
    }
    setDenominatorError(null);
    setDenominatorInput(parsed.join(', '));
    dispatch(setEquivalentDenominators(parsed));
    setPendingChanges(true);
  };

  const handleDenominatorInputChange = (value: string) => {
    setDenominatorInput(value);
    if (value.trim().length === 0) {
      setDenominatorError('Enter at least one positive integer denominator');
    } else {
      setDenominatorError(null);
    }
  };

  const handleIntegerLabelToggle = () => {
    dispatch(setEquivalentIntegerLabelsOnly(!equivalentFractions.integerLabelsOnly));
    setPendingChanges(true);
  };

  // Handle reset preferences
  const handleResetPreferences = () => {
    dispatch(resetPreferences());
    setPendingChanges(false);
    setShowResetConfirm(false);
  };

  // Theme options
  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun, description: 'Light theme with bright colours' },
    { value: 'dark' as const, label: 'Dark', icon: Moon, description: 'Dark theme with muted colours' },
  ];

  // Export background options
  const backgroundOptions = [
    { value: 'transparent' as const, label: 'Transparent', description: 'No background colour', preview: 'bg-gray-100 bg-opacity-50 bg-checkerboard' },
    { value: 'white' as const, label: 'White', description: 'White background', preview: 'bg-white border border-gray-200' },
    { value: 'black' as const, label: 'Black', description: 'Black background', preview: 'bg-black border border-gray-600' },
  ];

  // Orientation options
  const orientationOptions = [
    { value: 'horizontal' as const, label: 'Horizontal', description: 'Number line runs left to right' },
    { value: 'vertical' as const, label: 'Vertical', description: 'Number line runs top to bottom' },
  ];

  // Locale options
  const localeOptions = [
    { value: 'en-GB' as const, label: 'English (UK)', description: '1,234.56 format' },
    { value: 'en-US' as const, label: 'English (US)', description: '1,234.56 format' },
    { value: 'de-DE' as const, label: 'German', description: '1.234,56 format' },
    { value: 'fr-FR' as const, label: 'French', description: '1 234,56 format' },
  ];

  // Subdivision mode options for hierarchical ticks
  const subdivisionModeOptions: Array<{ value: SubdivisionMode; label: string; description: string }> = [
    { 
      value: 'smart', 
      label: 'Smart (Auto)', 
      description: 'Automatically chooses best subdivision based on context' 
    },
    { 
      value: 'decimal', 
      label: 'Decimal (Powers of 10)', 
      description: 'Shows 10ths, 100ths, 1000ths when zooming in' 
    },
    { 
      value: 'binary', 
      label: 'Binary (Halves)', 
      description: 'Shows halves, quarters, eighths, sixteenths' 
    },
    { 
      value: 'halves-tenths', 
      label: 'Halves then Tenths', 
      description: 'Shows halves, quarters, then switches to tenths' 
    },
  ];

  return (
    <div className="space-y-4">
      {/* Header with auto-save indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Preferences
          </h2>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            Customise your Number Line experience
          </p>
        </div>
        {pendingChanges && (
          <div className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
            <Save size={12} />
            Auto-saved
          </div>
        )}
      </div>

      {/* Section 1: Appearance */}
      <CollapsibleSection
        title="Appearance"
        icon={<Palette size={18} />}
        iconColorClass="text-blue-600 dark:text-blue-400"
        defaultOpen={false}
      >
        {/* Theme Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Theme
          </label>
          <div className="grid grid-cols-1 gap-3">
            {themeOptions.map((option) => {
              const Icon = option.icon;
              return (
                <label
                  key={option.value}
                  className={`
                    flex items-center p-3 border rounded-lg cursor-pointer transition-all
                    ${preferences.theme === option.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="theme"
                    value={option.value}
                    checked={preferences.theme === option.value}
                    onChange={(e) => handleThemeChange(e.target.value as ThemeMode)}
                    className="sr-only"
                  />
                  <Icon size={20} className={`mr-3 ${
                    preferences.theme === option.value
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400'
                  }`} />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {option.label}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {option.description}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Global Text Size */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Global Text Size
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0.75"
              max="1.5"
              step="0.05"
              value={preferences.textScale ?? 1}
              onChange={(e) => dispatch(setTextScale(parseFloat(e.target.value)))}
              className="flex-1"
            />
            <span className="text-sm font-mono px-2 py-1 rounded border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
              {(preferences.textScale ?? 1).toFixed(2)}×
            </span>
          </div>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            Scales the size of numbers, labels, and most text in the app
          </p>
        </div>
      </CollapsibleSection>

      {/* Section 2: Number Line */}
      <CollapsibleSection
        title="Number Line"
        icon={<Ruler size={18} />}
        iconColorClass="text-green-600 dark:text-green-400"
        defaultOpen={false}
      >
        {/* Preferred Orientation */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Preferred Orientation
          </label>
          <div className="grid grid-cols-2 gap-3">
            {orientationOptions.map((option) => (
              <label
                key={option.value}
                className={`
                  flex items-center p-3 border rounded-lg cursor-pointer transition-all
                  ${preferences.preferredOrientation === option.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="orientation"
                  value={option.value}
                  checked={preferences.preferredOrientation === option.value}
                  onChange={(e) => handleOrientationChange(e.target.value as 'horizontal' | 'vertical')}
                  className="sr-only"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Default Jump Size */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Default Jump Size
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0.1"
              max="100"
              step="0.1"
              value={jumpSizeInput}
              onChange={handleJumpSizeInputChange}
              onBlur={handleJumpSizeInputBlur}
              className="w-20 px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">units</span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Default amount to jump when using pan toolbar jump buttons
          </p>
        </div>

        {/* Tick Subdivision Mode */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Tick Subdivision Mode
          </label>
          <select
            value={preferences.subdivisionMode ?? 'smart'}
            onChange={(e) => {
              dispatch(setSubdivisionMode(e.target.value as SubdivisionMode));
              setPendingChanges(true);
            }}
            className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            {subdivisionModeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            {subdivisionModeOptions.find(o => o.value === (preferences.subdivisionMode ?? 'smart'))?.description}
          </p>
        </div>

        {/* Equivalent Fractions Mode */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Equivalent Fractions Mode
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {[
              { id: 'standard', label: 'Standard', description: 'Single number line' },
              { id: 'equivalentFractions', label: 'Equivalent Fractions', description: 'Stacked aligned lines' },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => handleViewModeChange(mode.id as 'standard' | 'equivalentFractions')}
                className={`p-3 rounded-lg border text-left transition-all ${
                  equivalentFractions.mode === mode.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <div className="font-medium text-gray-900 dark:text-white">{mode.label}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{mode.description}</div>
              </button>
            ))}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Denominators (comma-separated)
            </label>
            <input
              type="text"
              value={denominatorInput}
              onChange={(e) => handleDenominatorInputChange(e.target.value)}
              onBlur={() => handleDenominatorSave(denominatorInput)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleDenominatorSave(denominatorInput);
                }
              }}
              className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
              placeholder="e.g., 2,3,4,6,12"
            />
            {denominatorError && (
              <p className="text-xs text-red-500">{denominatorError}</p>
            )}
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Positive integers only, up to 12 values. Applies when equivalent fractions mode is active.
            </p>
          </div>

          <ToggleSwitch
            checked={equivalentFractions.integerLabelsOnly}
            onChange={handleIntegerLabelToggle}
            label="Label integer numerator ticks only"
            description="Show labels at multiples of 1/denominator using integer numerators."
          />
        </div>

        {/* Default Scale Factor */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Default Scale Factor (for double number lines)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="1"
              value={preferences.defaultScaleFactor.n}
              onChange={(e) => handleScaleFactorChange(parseInt(e.target.value) || 1, preferences.defaultScaleFactor.d)}
              className="w-16 px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            />
            <span className="text-gray-600 dark:text-gray-400">/</span>
            <input
              type="number"
              min="1"
              value={preferences.defaultScaleFactor.d}
              onChange={(e) => handleScaleFactorChange(preferences.defaultScaleFactor.n, parseInt(e.target.value) || 1)}
              className="w-16 px-2 py-1 text-sm border rounded bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
            />
          </div>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            Default scale factor when creating double number lines
          </p>
        </div>

        {/* Constants Precision */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Constants Precision (decimal places)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="2"
              max="6"
              value={preferences.constantsPrecision}
              onChange={(e) => handlePrecisionChange(parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-mono px-2 py-1 rounded border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
              {preferences.constantsPrecision}
            </span>
          </div>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            Number of decimal places shown for π, e, and other constants
          </p>
        </div>
      </CollapsibleSection>

      {/* Section 2.5: Jump Layout Settings */}
      <CollapsibleSection
        title="Jump Layout"
        icon={<Ruler size={18} />}
        iconColorClass="text-violet-600 dark:text-violet-400"
        defaultOpen={false}
      >
        <JumpLayoutSettings
          compact={false}
          isDark={preferences.theme === 'dark'}
          collapsible={false}
        />
      </CollapsibleSection>

      {/* Section 3: Navigation and Controls */}
      <CollapsibleSection
        title="Navigation and Controls"
        icon={<MousePointer2 size={18} />}
        iconColorClass="text-purple-600 dark:text-purple-400"
        defaultOpen={false}
      >
        <ToggleSwitch
          checked={preferences.wheelReverseScroll}
          onChange={() => dispatch(toggleWheelReverseScroll())}
          label="Reverse Scroll Direction (Pan)"
          description="Swap pan scroll direction (horizontal + vertical)"
        />

        {/* Scroll sensitivity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Scroll Sensitivity (Pan)
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="0.1"
              max="4"
              step="0.1"
              value={preferences.wheelScrollSensitivity}
              onChange={(e) => dispatch(setWheelScrollSensitivity(parseFloat(e.target.value)))}
              className="flex-1"
            />
            <span className="text-sm font-mono px-2 py-1 rounded border bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
              {preferences.wheelScrollSensitivity.toFixed(1)}x
            </span>
          </div>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            Adjust how far the view pans per wheel scroll; lower is slower, higher is faster
          </p>
        </div>

        <ToggleSwitch
          checked={preferences.keyboardShortcuts}
          onChange={() => dispatch(toggleKeyboardShortcuts())}
          label="Keyboard Shortcuts"
          description="Enable keyboard shortcuts for quick actions"
        />
      </CollapsibleSection>

      {/* Section 4: Regional Settings */}
      <CollapsibleSection
        title="Regional Settings"
        icon={<Share2 size={18} />}
        iconColorClass="text-orange-600 dark:text-orange-400"
        defaultOpen={false}
      >
        {/* Export Background - disabled/hidden for now */}
        {/* <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Export Background
          </label>
          <div className="grid grid-cols-1 gap-3">
            {backgroundOptions.map((option) => (
              <label
                key={option.value}
                className={`
                  flex items-center p-3 border rounded-lg cursor-pointer transition-all
                  ${preferences.exportBackground === option.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="exportBackground"
                  value={option.value}
                  checked={preferences.exportBackground === option.value}
                  onChange={(e) => handleExportBackgroundChange(e.target.value as 'transparent' | 'white' | 'black')}
                  className="sr-only"
                />
                <div className={`w-6 h-6 rounded border mr-3 ${option.preview}`}></div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {option.description}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div> */}

        {/* Locale */}
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
            Locale and Number Format
          </label>
          <select
            value={preferences.locale}
            onChange={(e) => dispatch(setLocale(e.target.value as LocaleCode))}
            className="w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white"
          >
            {localeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label} - {option.description}
              </option>
            ))}
          </select>
          <p className="text-xs mt-1 text-gray-600 dark:text-gray-400">
            Number formatting and decimal separator preferences
          </p>
        </div>
      </CollapsibleSection>

      {/* Section 5: Accessibility and App */}
      <CollapsibleSection
        title="Accessibility and App"
        icon={<Shield size={18} />}
        iconColorClass="text-teal-600 dark:text-teal-400"
        defaultOpen={false}
      >
        <ToggleSwitch
          checked={preferences.accessibilityMode}
          onChange={() => dispatch(toggleAccessibilityMode())}
          label="Accessibility Mode"
          description="Enhanced focus indicators and screen reader support"
        />

        <ToggleSwitch
          checked={preferences.autoSave}
          onChange={() => dispatch(toggleAutoSave())}
          label="Auto-save"
          description="Automatically save your work as you make changes"
        />

        <ToggleSwitch
          checked={preferences.sidebarDefaultOpen ?? true}
          onChange={() => {
            dispatch(setSidebarDefaultOpen(!(preferences.sidebarDefaultOpen ?? true)));
            setPendingChanges(true);
          }}
          label="Sidebar Starts Open (Desktop)"
          description="When the app opens on larger screens, keep the left toolbar expanded"
        />

        <ToggleSwitch
          checked={preferences.showInstallPrompt}
          onChange={() => dispatch(toggleInstallPrompt())}
          label="Show Install Prompt"
          description="Show the install prompt when the app is available for installation"
        />

        {/* Install button */}
        <div className="pt-2">
          <InstallButton variant="secondary" size="md">
            Install Number Line
          </InstallButton>
        </div>
      </CollapsibleSection>

      {/* Section 6: Session Info (collapsed by default) */}
      <CollapsibleSection
        title="Session Info"
        icon={<BarChart2 size={18} />}
        iconColorClass="text-indigo-600 dark:text-indigo-400"
        defaultOpen={false}
      >
        <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
          <div className="flex justify-between">
            <span>Scale (pixels per unit)</span>
            <span className="font-mono">{Math.round(viewport.scale)}px</span>
          </div>
          <div className="flex justify-between">
            <span>Secondary line</span>
            <span className="font-mono">{secondaryLine.enabled ? 'On' : 'Off'}</span>
          </div>
          <div className="flex justify-between">
            <span>Visible constants</span>
            <span className="font-mono">{constants.visibleConstants.length}</span>
          </div>
        </div>
      </CollapsibleSection>

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setShowResetConfirm(true)}
          className="inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md transition-colors text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RotateCcw size={16} className="mr-2" />
          Reset to Defaults
        </button>
      </div>

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="rounded-lg shadow-xl max-w-md w-full mx-4 bg-white dark:bg-gray-800">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                Reset Preferences
              </h3>
              <p className="text-sm mb-4 text-gray-600 dark:text-gray-400">
                Are you sure you want to reset all preferences to their default values? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  className="px-3 py-2 text-sm font-medium border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetPreferences}
                  className="px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
                >
                  Reset All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
